import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import tailwindClassnames from "../common/TailwindClassnames";
import ConfirmationModal from "../common/ConfirmationModal";
import { getModels } from "../../api/modelApi";
import {
  disableModelForProject,
  enableModelForProject,
} from "../../api/projectsApi";
import { EnabledModelItem, ModelResponseDto } from "../common/ObjectTypes";
import { buildModelEnablementRows } from "./aiMappingsHelpers";

type ModelEnablementTableProps = {
  projectId: number;
  /** This project's current enabled-models list (enabled or disabled) — the
   *  single source of truth for enablement state, owned by the parent so it
   *  stays in sync with the mappings fetch and the Model column. */
  enabledModels: EnabledModelItem[];
  canManage: boolean;
  /** Called after a successful enable/disable/re-enable with the updated
   *  item, so the parent can upsert it into `enabledModels`. */
  onModelUpdated: (model: EnabledModelItem) => void;
};

/**
 * Per-project model enablement table: one row per model (every globally-active
 * model, unioned with any model this project has enabled even if it's since
 * gone globally inactive or been disabled), showing its enablement status and
 * an Enable/Disable action. Replaces the old dropdown-driven "Enable a model"
 * picker modal — enabling and disabling both happen inline, per row. Disabling
 * asks for confirmation (reversible — re-enabling, or simply re-running the
 * model, restores it); enabling does not, matching this app's existing
 * reversible-action conventions (e.g. "Exclude" in AiMappingsLabelTable).
 * @param projectId numeric project id
 * @param enabledModels the project's current enabled-models list
 * @param canManage whether the current user can enable/disable (MANAGE_AI_MAPPINGS)
 * @param onModelUpdated called with the updated item after enable/disable
 */
const ModelEnablementTable: React.FC<ModelEnablementTableProps> = ({
  projectId,
  enabledModels,
  canManage,
  onModelUpdated,
}) => {
  const { t } = useTranslation();

  const [globalModels, setGlobalModels] = useState<ModelResponseDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [pendingModelId, setPendingModelId] = useState<number | null>(null);
  const [savingModelId, setSavingModelId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const loadGlobalModels = async () => {
      setLoading(true);
      try {
        // Matches ManageAiModelZooPage's own getModels() call — the default
        // page size (25) would silently truncate this table once the active
        // model count exceeds it.
        const resp = await getModels({ skip: 0, take: 100 });
        if (cancelled) return;
        setGlobalModels(resp.models);
        setLoadError(false);
      } catch (error) {
        if (cancelled) return;
        setGlobalModels([]);
        setLoadError(true);
        console.error("Error loading models:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadGlobalModels();
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = useMemo(
    () => buildModelEnablementRows(globalModels, enabledModels),
    [globalModels, enabledModels],
  );
  const pendingRow =
    rows.find((row) => row.model_id === pendingModelId) ?? null;

  const handleEnable = async (modelId: number) => {
    if (savingModelId != null) return;
    setSavingModelId(modelId);
    try {
      const enabled = await enableModelForProject(projectId, modelId);
      toast.success(t("Model enabled"));
      onModelUpdated(enabled);
    } catch (error: any) {
      console.error("Error enabling model for project:", error);
      if (error?.status) {
        toast.error(error.message);
      }
    } finally {
      setSavingModelId(null);
    }
  };

  const confirmDisable = async () => {
    if (pendingModelId == null || savingModelId != null) return;
    setSavingModelId(pendingModelId);
    try {
      const disabled = await disableModelForProject(projectId, pendingModelId);
      toast.success(t("Model disabled"));
      onModelUpdated(disabled);
      setPendingModelId(null);
    } catch (error: any) {
      console.error("Error disabling model for project:", error);
      if (error?.status) {
        toast.error(error.message);
      }
    } finally {
      setSavingModelId(null);
    }
  };

  return (
    <div>
      {loading ? (
        <p className={tailwindClassnames.basicLabel}>{t("Loading")}...</p>
      ) : rows.length === 0 ? (
        <p className={tailwindClassnames.basicLabel}>
          {loadError
            ? t("Could not load models. Please try again.")
            : t("No models are available.")}
        </p>
      ) : (
        <div className="overflow-x-auto">
          {/* The global active-model list failed to load, but rows still
              exist from `enabledModels` (passed down from the container) --
              keep the table (and its Enable/Disable actions) usable rather
              than hiding it behind the error. */}
          {loadError && (
            <p className={`${tailwindClassnames.basicLabel} mb-2`}>
              {t("Could not load models. Please try again.")}
            </p>
          )}
          <table className={tailwindClassnames.tableContainer}>
            <thead className={tailwindClassnames.tableHead}>
              <tr>
                <th className={tailwindClassnames.tableHeadText}>
                  {t("Model")}
                </th>
                <th className={tailwindClassnames.tableHeadText}>
                  {t("Status")}
                </th>
                {canManage && (
                  <th className={tailwindClassnames.tableHeadText}>
                    {t("Actions")}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.model_id} className={tailwindClassnames.noHover}>
                  <td className={tailwindClassnames.tableBodyText}>
                    <span className="inline-flex items-center gap-1.5">
                      <span>{row.name}</span>
                      {row.globalStatus != null &&
                        row.globalStatus !== "active" && (
                          <span
                            className={tailwindClassnames.inactiveModelBadge}
                          >
                            {t("Inactive")}
                          </span>
                        )}
                    </span>
                  </td>
                  <td className={tailwindClassnames.tableBodyText}>
                    {row.enablementStatus === "enabled" && (
                      <span className={tailwindClassnames.chipActive}>
                        {t("Enabled")}
                      </span>
                    )}
                    {row.enablementStatus === "disabled" && (
                      <span className={tailwindClassnames.chipFailed}>
                        {t("Disabled")}
                      </span>
                    )}
                    {row.enablementStatus === "not_enabled" && (
                      <span className={tailwindClassnames.chipInactive}>
                        {t("Not enabled")}
                      </span>
                    )}
                  </td>
                  {canManage && (
                    <td className={tailwindClassnames.tableBodyText}>
                      {row.enablementStatus === "enabled" ? (
                        <button
                          type="button"
                          className="text-primary-orange-dark hover:underline text-sm font-semibold disabled:opacity-50"
                          onClick={() => setPendingModelId(row.model_id)}
                          disabled={savingModelId != null}
                          data-testid={`disable-model-${row.model_id}`}
                        >
                          {t("Disable")}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="text-sky-blue-dark dark:text-sky-blue hover:underline text-sm font-semibold disabled:opacity-50"
                          onClick={() => handleEnable(row.model_id)}
                          disabled={savingModelId != null}
                          data-testid={`enable-model-${row.model_id}`}
                        >
                          {t("Enable")}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmationModal
        isOpen={pendingRow != null}
        confirmationMessage={t(
          "Are you sure you want to disable this model? Any predictions from this model will no longer appear alongside photos. Saved observations will not be affected, and re-running this model within the project will re-enable it.",
        )}
        onConfirm={confirmDisable}
        onCancel={() => setPendingModelId(null)}
        confirmLabel={t("Disable")}
        saving={savingModelId === pendingModelId}
      />
    </div>
  );
};

export default ModelEnablementTable;
